import React, { useEffect, useMemo, useState } from "react";
import supabase from "../supabaseClient";
import "../pages/Dashboard.css";

const Dashboard = () => {
  const [checkIns, setCheckIns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [operatorEmail, setOperatorEmail] = useState(null);
  const [carwashId, setCarwashId] = useState(null);
  const [plateByUser, setPlateByUser] = useState({}); // { [user_id]: plate_number | null }

  // Helper: fetch plates for a list of userIds and merge into state map
  const fetchPlatesForUsers = async (userIds) => {
    const unique = Array.from(new Set(userIds)).filter(Boolean);
    if (unique.length === 0) return;

    const { data, error } = await supabase
      .from("memberships")
      .select("user_id, plate_number")
      .in("user_id", unique);

    if (error) {
      console.error("Error fetching membership plates:", error);
      return;
    }

    const map = {};
    for (const row of data ?? []) {
      map[row.user_id] = row.plate_number || null;
    }
    setPlateByUser((prev) => ({ ...prev, ...map }));
  };

  useEffect(() => {
    const fetchCarwashInfo = async () => {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError || !user) {
        console.error("User not found:", userError);
        return;
      }

      setOperatorEmail(user.email);

      const { data: userRow, error: roleError } = await supabase
        .from("users")
        .select("role")
        .eq("id", user.id)
        .single();

      if (roleError || !userRow || userRow.role !== "car_wash_staff") {
        alert("Unauthorized. This portal is only for car wash staff.");
        await supabase.auth.signOut();
        window.location.href = "/";
        return;
      }

      const { data: carwash, error: carwashError } = await supabase
        .from("carwashes")
        .select("id, name")
        .eq("user_id", user.id)
        .single();

      if (carwashError || !carwash) {
        console.error("No carwash found for this user:", carwashError);
        return;
      }

      setCarwashId(carwash.id);
    };

    fetchCarwashInfo();
  }, []);

  useEffect(() => {
    if (!carwashId) return;

    const fetchCheckIns = async () => {
      setLoading(true);
      const today = new Date().toISOString().slice(0, 10);

      const { data, error } = await supabase
        .from("check_ins")
        .select("*")
        .eq("carwash_id", carwashId)
        .gte("check_in_time", today)
        .order("check_in_time", { ascending: false });

      if (error) {
        console.error("Error fetching check-ins:", error);
        setLoading(false);
        return;
      }

      setCheckIns(data || []);
      setLoading(false);

      // fetch membership plates for these user_ids
      const userIds = (data || []).map((c) => c.user_id);
      fetchPlatesForUsers(userIds);
    };

    fetchCheckIns();

    const channel = supabase
      .channel("realtime-checkins")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "check_ins",
          filter: `carwash_id=eq.${carwashId}`,
        },
        async (payload) => {
          const newCheckIn = payload.new;
          setCheckIns((prev) => {
            const exists = prev.some((item) => item.id === newCheckIn.id);
            if (exists) return prev;
            return [newCheckIn, ...prev];
          });

          // ensure we have the plate for this user in the map
          if (!plateByUser[newCheckIn.user_id]) {
            fetchPlatesForUsers([newCheckIn.user_id]);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carwashId]);

  const pendingCheckIns = useMemo(
    () => checkIns.filter((c) => !c.validated),
    [checkIns]
  );
  const validatedCount = useMemo(
    () => checkIns.filter((c) => c.validated).length,
    [checkIns]
  );

  const validateCheckIn = async (checkIn) => {
    const plate = plateByUser[checkIn.user_id] || "—";
    const confirmed = window.confirm(
      `Please check the plate before validating.\n\nPlate on screen: ${plate}\n\nProceed to validate?`
    );
    if (!confirmed) return;

    const { error } = await supabase
      .from("check_ins")
      .update({ validated: true })
      .eq("id", checkIn.id);

    if (error) {
      alert("Error validating check-in.");
    } else {
      setCheckIns((prev) =>
        prev.map((item) =>
          item.id === checkIn.id ? { ...item, validated: true } : item
        )
      );
    }
  };

  return (
    <div className="dashboard">
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Today's Activity</h3>
          {operatorEmail && (
            <div className="user-info">
              <span className="user-email">{operatorEmail}</span>
            </div>
          )}
        </div>

        <div className="stats-container">
          <div className="stat-card">
            <div className="stat-value">{validatedCount}</div>
            <div className="stat-label">Validated Today</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{pendingCheckIns.length}</div>
            <div className="stat-label">Pending Validation</div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Pending Check-Ins</h3>
        </div>

        {/* 🔔 Staff reminder */}
        <div className="notice">
          <strong>Reminder:</strong> Please check the plate before validating.
        </div>

        {loading ? (
          <div className="loading-spinner">Loading...</div>
        ) : pendingCheckIns.length === 0 ? (
          <div className="empty-state">
            <p>All check-ins are validated. Great job!</p>
          </div>
        ) : (
          <ul className="checkin-list">
            {pendingCheckIns.map((checkIn) => {
              const plate = plateByUser[checkIn.user_id] || "—";
              return (
                <li key={checkIn.id} className="checkin-item">
                  <div className="checkin-details">
                    <div className="checkin-user">
                      <span className="user-avatar">
                        {checkIn.username?.charAt(0)?.toUpperCase() || "U"}
                      </span>
                      <span className="user-name">{checkIn.username}</span>
                    </div>

                    {/* Plate from MEMBERSHIPS */}
                    <div className="checkin-plate">
                      Plate: <span className="plate-badge">{plate}</span>
                    </div>

                    <div className="checkin-time">
                      {new Date(checkIn.check_in_time).toLocaleTimeString()}
                    </div>
                  </div>

                  <button
                    onClick={() => validateCheckIn(checkIn)}
                    className="button button-primary"
                  >
                    Validate
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
